/**
 * A mesa de trabalho de quem audita glosa.
 *
 * O recorte que o auditor precisa não é "o que foi glosado" — é **o que foi
 * glosado ao lado do que foi pago**, no mesmo evento. Um item recusado em
 * R$ 4,50 numa guia que recebeu R$ 144,00 é uma coisa; o mesmo R$ 4,50 numa
 * guia que recebeu R$ 4,50 é outra, e só a proporção distingue as duas.
 *
 * Por isso o universo é a **guia que tem alguma glosa**, e dentro dela vão
 * TODOS os itens, inclusive os pagos integralmente. Mostrar só os glosados
 * responderia "quanto glosaram"; mostrar a guia inteira responde "vale
 * recorrer".
 *
 * A hierarquia que o usuário pediu — arquivo › protocolo › guia › item —
 * aparece como contexto do cabeçalho de cada evento, não como árvore de
 * navegação: auditor filtra e ordena, não expande nós.
 *
 * Por que página e não modal: com pago e glosado são nove colunas, o auditor
 * passa tempo aqui, e uma URL com os filtros pode ser salva e repassada.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import { auditoriaGlosa, filtrosDeGlosa } from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Badge,
  Botao,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Painel,
  Paginacao,
  SelecaoBuscavel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { brl, formatDate, numero } from '../utils/formato';

const TAMANHO = 25;

/* Uma célula de valor que fica VAZIA quando não houve glosa.
 *
 * "R$ 0,00" numa coluna de glosa se lê como "foi analisado e nada foi
 * recusado" — afirmação diferente de "não houve glosa aqui". A diferença
 * importa para quem decide recorrer. */
function Glosado({ valor }) {
  if (valor === null || valor === undefined) {
    return <span style={{ color: 'var(--ff-border)' }}>—</span>;
  }
  return <strong style={{ color: 'var(--ff-perigo, #b42318)' }}>{brl(valor)}</strong>;
}

function Evento({ ev, aberto, aoAlternar }) {
  // Vem calculado do servidor: é por ele que o filtro de percentual corta, e
  // recalcular aqui abriria espaço para a tela discordar do filtro.
  const proporcao = Math.round(ev.percentual ?? 0);
  const glosados = ev.itens.filter((i) => i.valor_glosado).length;

  return (
    <div
      style={{
        border: '1px solid var(--ff-border-soft)',
        borderRadius: 12,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      {/* O contexto do evento: arquivo › protocolo › guia, mais a proporção,
          que é o número que decide se vale recorrer. */}
      <button
        type="button"
        className="d-flex flex-wrap gap-3 align-items-center justify-content-between p-3 w-100"
        style={{
          background: 'var(--ff-fundo-suave, #f8fafc)',
          border: 0,
          textAlign: 'left',
          cursor: 'pointer',
        }}
        onClick={aoAlternar}
        aria-expanded={aberto}
      >
        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
          <i
            className={`bi bi-chevron-${aberto ? 'down' : 'right'}`}
            aria-hidden="true"
            style={{ color: 'var(--ff-muted)' }}
          />
          <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
            {ev.arquivo || 'sem arquivo de pagamento'}
            {ev.competencia ? ` · ${ev.competencia}` : ''}
            {' › '}
            {ev.protocolo || 'sem protocolo'}
          </div>
            <div className="ff-mono" style={{ fontWeight: 600, fontSize: 15 }}>
              guia {ev.guia}
              <span
                style={{ fontWeight: 400, fontSize: 13, color: 'var(--ff-muted)' }}
              >
                {'  '}
                {glosados} de {ev.itens.length} procedimento(s) glosado(s)
              </span>
            </div>
          </div>
        </div>
        <div className="d-flex gap-4 align-items-center">
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>Pago</div>
            <div className="ff-mono">{brl(ev.pago)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>Glosado</div>
            <div className="ff-mono" style={{ color: 'var(--ff-perigo, #b42318)' }}>
              {brl(ev.glosado)}
            </div>
          </div>
          <Badge tom={proporcao >= 50 ? 'reprovado' : 'atencao'}>{proporcao}% glosado</Badge>
        </div>
      </button>

      {/* Recolhida por padrão. Com 52 guias de ~15 itens, tudo aberto é
          injogável — e o cabeçalho já traz o que decide se vale abrir. */}
      {!aberto ? null : (
      <div style={{ overflowX: 'auto' }}>
        <Tabela estreita>
          <thead>
            <tr>
              <Th>Procedimento</Th>
              <Th>Atendimento</Th>
              <Th alinhar="right">Qtd</Th>
              <Th alinhar="right">Unitário</Th>
              <Th alinhar="right">Pago</Th>
              <Th alinhar="right">Glosado</Th>
              <Th>Causa</Th>
            </tr>
          </thead>
          <tbody>
            {ev.itens.map((i, n) => (
              <tr key={`${i.codigo_servico}-${n}`}>
                <td className="col-principal">
                  <span className="ff-mono">{i.codigo_servico || '—'}</span>
                  <span
                    style={{ display: 'block', fontSize: 13, color: 'var(--ff-muted)' }}
                  >
                    {i.descricao_servico || 'nome não disponível'}
                  </span>
                </td>
                <td className="col-data">{formatDate(i.data_atendimento)}</td>
                <td className="col-qtd">{i.quantidade ?? '—'}</td>
                <td className="col-valor">{i.valor_unitario ? brl(i.valor_unitario) : '—'}</td>
                <td className="col-valor">
                  {i.valor_pago === null ? (
                    <span style={{ color: 'var(--ff-border)' }}>—</span>
                  ) : (
                    brl(i.valor_pago)
                  )}
                </td>
                <td className="col-valor">
                  <Glosado valor={i.valor_glosado} />
                </td>
                <td>
                  {i.causas.length === 0 ? (
                    <span style={{ color: 'var(--ff-border)' }}>—</span>
                  ) : (
                    i.causas.map((c, k) => (
                      <div key={k} style={{ marginBottom: k < i.causas.length - 1 ? 6 : 0 }}>
                        <span className="ff-mono">{c.codigo_causa || '—'}</span>
                        {c.marcador && (
                          <span style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                            {' '}
                            {c.marcador}
                          </span>
                        )}
                        <span
                          style={{
                            display: 'block',
                            fontSize: 13,
                            color: 'var(--ff-muted)',
                          }}
                        >
                          {c.motivo}
                        </span>
                      </div>
                    ))
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      </div>
      )}
    </div>
  );
}

export default function AuditoriaGlosa() {
  const [params, setParams] = useSearchParams();
  const [dados, setDados] = useState(null);
  const [opcoes, setOpcoes] = useState(null);
  const [erro, setErro] = useState('');
  const [guia, setGuia] = useState(params.get('guia') || '');
  const [abertas, setAbertas] = useState(() => new Set());
  // O slider mexe muito; a consulta só sai quando o dedo solta. Sem isso
  // seria uma requisição por pixel arrastado.
  const [minimo, setMinimo] = useState(Number(params.get('glosa_minima') || 0));

  // Os filtros vivem na URL: o auditor salva o recorte, volta nele depois e
  // manda para outra pessoa sem precisar explicar como chegou lá.
  const filtros = useMemo(
    () => ({
      competencia: params.get('competencia') || undefined,
      id_arquivo: params.get('id_arquivo') || undefined,
      protocolo: params.get('protocolo') || undefined,
      causa: params.get('causa') || undefined,
      codigo_servico: params.get('codigo_servico') || undefined,
      guia: params.get('guia') || undefined,
      glosa_minima: params.get('glosa_minima') || undefined,
      ordem: params.get('ordem') || undefined,
      pagina: Number(params.get('pagina') || 1),
      tamanho: TAMANHO,
    }),
    [params]
  );

  const trocar = useCallback(
    (campo, valor) => {
      const novo = new URLSearchParams(params);
      if (valor) novo.set(campo, valor);
      else novo.delete(campo);
      if (campo !== 'pagina') novo.delete('pagina');
      setParams(novo);
    },
    [params, setParams]
  );

  // As opções seguem o recorte: escolher um protocolo reduz as causas às que
  // de fato ocorrem nele. Recarrega junto com os dados, pelos mesmos filtros.
  useEffect(() => {
    filtrosDeGlosa(filtros)
      .then((r) => setOpcoes(r.data))
      .catch(() => setOpcoes({ competencias: [], arquivos: [], protocolos: [], causas: [] }));
  }, [filtros]);

  useEffect(() => {
    setDados(null);
    setAbertas(new Set());
    auditoriaGlosa(filtros)
      .then((r) => {
        setDados(r.data);
        // Filtrou até sobrar uma guia? Abre: foi o que a pessoa procurou.
        if (r.data.guias.length === 1) {
          setAbertas(new Set([r.data.guias[0].guia]));
        }
      })
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar a auditoria.')));
  }, [filtros]);

  const algumFiltro = [
    'competencia',
    'id_arquivo',
    'protocolo',
    'causa',
    'codigo_servico',
    'guia',
    'glosa_minima',
  ].some((c) => params.get(c));

  return (
    <>
      <PageHeader
        titulo="Auditoria de glosa"
        subtitulo="Evento por evento, item a item — o que foi pago e o que foi recusado, lado a lado."
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {dados && (
        <div className="ff-grid-tiles mb-4">
          <Indicador
            natureza="neutro"
            rotulo="Guias com glosa"
            valor={numero(dados.resumo.guias)}
            nota={`${numero(dados.resumo.itens_glosados)} procedimento(s) recusado(s) nesta página`}
          />
          <Indicador
            natureza="entrada"
            rotulo="Pago nessas guias"
            valor={brl(dados.resumo.pago)}
            nota="o que entrou apesar da glosa"
          />
          <Indicador
            natureza="atencao"
            rotulo="Glosado"
            valor={brl(dados.resumo.glosado)}
            nota="o que a operadora recusou"
          />
          {/* A proporção é o número que orienta o esforço: diz quanto do que
              foi faturado nessas guias deixou de entrar. */}
          <Indicador
            natureza="financeiro"
            rotulo="% recusado"
            valor={`${
              Number(dados.resumo.pago) + Number(dados.resumo.glosado) > 0
                ? Math.round(
                    (Number(dados.resumo.glosado) /
                      (Number(dados.resumo.pago) + Number(dados.resumo.glosado))) *
                      100
                  )
                : 0
            }%`}
            nota="do faturado nessas guias"
          />
        </div>
      )}

      <Painel
        titulo="Filtros"
        acoes={
          algumFiltro && (
            <Botao
              variante="secundario"
              pequeno
              onClick={() => {
                setMinimo(0);
                setGuia('');
                setParams(new URLSearchParams());
              }}
            >
              Limpar filtros
            </Botao>
          )
        }
      >
        <div className="p-3 d-flex flex-wrap gap-3">
          <SelecaoBuscavel
            rotulo="Competência"
            largura={150}
            textoTodas="Todas"
            valor={params.get('competencia') || ''}
            opcoes={(opcoes?.competencias || []).map((c) => ({ valor: c, texto: c }))}
            aoMudar={(v) => trocar('competencia', v)}
          />

          <SelecaoBuscavel
            rotulo="Arquivo"
            largura={210}
            textoTodas="Todos"
            valor={params.get('id_arquivo') || ''}
            opcoes={(opcoes?.arquivos || []).map((a) => ({
              valor: a.id_arquivo,
              texto: a.nome,
            }))}
            aoMudar={(v) => trocar('id_arquivo', v)}
          />

          <SelecaoBuscavel
            rotulo="Protocolo"
            largura={185}
            textoTodas="Todos"
            valor={params.get('protocolo') || ''}
            opcoes={(opcoes?.protocolos || []).map((p) => ({ valor: p, texto: p }))}
            aoMudar={(v) => trocar('protocolo', v)}
          />

          <SelecaoBuscavel
            rotulo="Causa"
            largura={260}
            textoTodas="Todas"
            placeholder="Buscar código ou motivo…"
            valor={params.get('causa') || ''}
            opcoes={(opcoes?.causas || []).map((c) => ({
              valor: c.codigo,
              texto: `${c.codigo} — ${c.motivo || 'sem motivo informado'}`,
            }))}
            aoMudar={(v) => trocar('causa', v)}
            ajuda={
              <Ajuda titulo="Causa da glosa" origem="Extrato de glosa da operadora">
                O código da operadora, com o motivo que ela imprimiu. A lista traz só as
                causas que ocorrem no recorte atual — se você filtrou um protocolo, só
                aparecem as causas daquele protocolo.
              </Ajuda>
            }
          />

          {/* O corte por percentual fica no SERVIDOR: cortar no cliente
              filtraria só a página carregada, e o auditor veria "3
              resultados" sem saber se há trezentos atrás.

              O valor corrente é um INDICADOR, não um botão: antes ele dividia
              espaço com os atalhos e os três pareciam a mesma coisa — não
              dava para saber qual era o estado e quais eram as ações. */}
          <div style={{ minWidth: 300, flex: '1 1 300px' }}>
            <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
              Glosa por guia
              <Ajuda titulo="% glosado da guia" origem="Pago e glosado da própria guia">
                Quanto do valor da guia foi recusado. Arraste para ver só os casos mais
                concentrados: no primeiro arquivo real, 6 guias acima de 50% respondem
                por R$ 364 dos R$ 656 glosados. O corte é sobre a guia inteira, mesmo
                quando há filtro de causa.
              </Ajuda>
            </label>

            <div className="d-flex align-items-center gap-3">
              <input
                type="range"
                className="form-range"
                min={0}
                max={100}
                step={5}
                value={minimo}
                onChange={(e) => setMinimo(Number(e.target.value))}
                onMouseUp={() => trocar('glosa_minima', minimo || '')}
                onTouchEnd={() => trocar('glosa_minima', minimo || '')}
                onKeyUp={() => trocar('glosa_minima', minimo || '')}
                aria-label="Percentual mínimo glosado da guia"
                style={{ flex: 1 }}
              />
              <div
                style={{
                  background: 'var(--ff-fundo-suave, #f1f5f9)',
                  border: '1px solid var(--ff-border-soft)',
                  borderRadius: 10,
                  padding: '4px 12px',
                  textAlign: 'center',
                  minWidth: 78,
                  lineHeight: 1.1,
                }}
              >
                <div style={{ fontSize: 10, color: 'var(--ff-muted)' }}>a partir de</div>
                <div className="ff-mono" style={{ fontSize: 17, fontWeight: 600 }}>
                  {minimo}%
                </div>
              </div>
            </div>

            {/* Os atalhos vêm abaixo, pequenos: são AÇÕES, e precisam se
                distinguir do valor corrente, que é estado. */}
            <div className="d-flex flex-wrap gap-1 mt-1">
              {[0, 10, 30, 50, 80, 100].map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`ff-btn ${
                    minimo === v ? 'ff-btn--primario' : 'ff-btn--secundario'
                  }`}
                  style={{ padding: '1px 9px', fontSize: 11, borderRadius: 999 }}
                  onClick={() => {
                    setMinimo(v);
                    trocar('glosa_minima', v || '');
                  }}
                >
                  {v === 0 ? 'todas' : v === 100 ? 'total' : `${v}%`}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>Ordenar por</label>
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 150, borderRadius: 8 }}
              value={params.get('ordem') || 'glosado'}
              onChange={(e) => trocar('ordem', e.target.value)}
            >
              <option value="glosado">Maior valor glosado</option>
              <option value="percentual">Maior % glosado</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>Guia</label>
            <div className="d-flex gap-1">
              <input
                className="form-control form-control-sm"
                style={{ width: 150, borderRadius: 8 }}
                placeholder="número da guia"
                value={guia}
                onChange={(e) => setGuia(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && trocar('guia', guia.trim())}
              />
              <Botao variante="secundario" pequeno onClick={() => trocar('guia', guia.trim())}>
                Buscar
              </Botao>
            </div>
          </div>
        </div>
      </Painel>

      <div className="mt-3">
        {!dados ? (
          <LoadingSpinner />
        ) : dados.guias.length === 0 ? (
          <Painel>
            <Vazio
              icone="bi-scissors"
              titulo="Nenhuma guia glosada neste recorte"
              descricao={
                algumFiltro
                  ? 'Tente afrouxar os filtros.'
                  : 'Nenhum arquivo de glosa foi lançado ainda.'
              }
            />
          </Painel>
        ) : (
          <>
            {/* O contador no TOPO: a paginação existe desde o começo, mas
                vivia só no rodapé, e com a tela comprida ninguém a via. */}
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-2">
              <span style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                Mostrando{' '}
                <strong>
                  {(dados.paginacao.pagina - 1) * dados.paginacao.tamanho + 1}–
                  {Math.min(
                    dados.paginacao.pagina * dados.paginacao.tamanho,
                    dados.paginacao.total
                  )}
                </strong>{' '}
                de <strong>{numero(dados.paginacao.total)}</strong> guias
              </span>
              <div className="d-flex gap-2">
                <Botao
                  variante="secundario"
                  pequeno
                  onClick={() => setAbertas(new Set(dados.guias.map((e) => e.guia)))}
                >
                  Expandir todas
                </Botao>
                <Botao variante="secundario" pequeno onClick={() => setAbertas(new Set())}>
                  Recolher todas
                </Botao>
              </div>
            </div>

            {dados.guias.map((ev) => (
              <Evento
                key={ev.guia}
                ev={ev}
                aberto={abertas.has(ev.guia)}
                aoAlternar={() =>
                  setAbertas((atual) => {
                    const novo = new Set(atual);
                    if (novo.has(ev.guia)) novo.delete(ev.guia);
                    else novo.add(ev.guia);
                    return novo;
                  })
                }
              />
            ))}
            <Paginacao
              pagina={dados.paginacao.pagina}
              totalPaginas={Math.ceil(dados.paginacao.total / dados.paginacao.tamanho)}
              total={dados.paginacao.total}
              de={(dados.paginacao.pagina - 1) * dados.paginacao.tamanho + 1}
              ate={Math.min(
                dados.paginacao.pagina * dados.paginacao.tamanho,
                dados.paginacao.total
              )}
              tamanho={dados.paginacao.tamanho}
              irPara={(p) => trocar('pagina', String(p))}
              rotulo="guias"
            />
          </>
        )}
      </div>
    </>
  );
}
